import { useEffect } from "react"
import { motion, AnimatePresence } from "motion/react"
import { X } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"

// Generic confirm dialog used in place of `window.confirm()` so the
// wizard's destructive prompts (remove section, delete component, etc.)
// match the rest of the design system instead of dropping into the
// browser's native chrome. Same overlay primitives as ConfirmLeave;
// both could be folded into one component later, but keeping them
// separate avoids regressing the leave-confirm UX while iterating
// here.
//
// The `destructive` variant turns the confirm button red so it
// telegraphs "this removes data."

interface ConfirmDialogProps {
  open: boolean
  title: string
  body: string
  cancelLabel?: string
  confirmLabel: string
  destructive?: boolean
  onCancel: () => void
  onConfirm: () => void
}

export function ConfirmDialog({
  open,
  title,
  body,
  cancelLabel = "Cancel",
  confirmLabel,
  destructive = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // Esc-to-cancel for keyboard users.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onCancel])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center px-4 pb-4 sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <button
            type="button"
            aria-label={cancelLabel}
            onClick={onCancel}
            className="absolute inset-0 bg-zinc-900/55 backdrop-blur-[2px]"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="relative z-10 w-full sm:max-w-md bg-white rounded-3xl shadow-2xl shadow-zinc-900/30"
          >
            <div
              className="px-5 py-5 sm:px-6 sm:py-6"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 20px)" }}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3
                  id="confirm-dialog-title"
                  className="text-[18px] font-bold text-zinc-900 tracking-tight leading-tight"
                >
                  {title}
                </h3>
                <button
                  type="button"
                  aria-label={cancelLabel}
                  onClick={onCancel}
                  className="size-8 rounded-lg hover:bg-zinc-100 grid place-items-center text-zinc-500"
                >
                  <X className="size-4" aria-hidden />
                </button>
              </div>
              <p className="text-[13.5px] text-zinc-500 leading-relaxed mb-5">
                {body}
              </p>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <button
                  type="button"
                  onClick={onCancel}
                  className="h-11 px-4 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[13.5px] font-semibold text-zinc-800 transition-colors duration-150"
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  className={cx(
                    "h-11 px-4 rounded-xl text-white text-[13.5px] font-semibold transition-colors duration-150",
                    destructive
                      ? "bg-rose-600 hover:bg-rose-700 shadow-sm shadow-rose-600/20"
                      : "bg-zinc-900 hover:bg-black",
                  )}
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
